"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { useEffect, useRef, useState } from "react";
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import { Quote } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
const testimonials = [{
  id: 1,
  quote: "The Mass is a transformative experience that stays with you long after the final curtain. Philip Gray's vision is both bold and deeply moving.",
  author: "Sarah Johnson",
  role: "Theater Critic",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1503120/img/image_lfkrqa9n.jpg"
}, {
  id: 2,
  quote: "An immersive journey that challenges and captivates. The production design and performances are nothing short of extraordinary.",
  author: "Marcus Rolle",
  role: "Arts Enthusiast",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1503120/img/image_9urypkze.jpg"
}, {
  id: 3,
  quote: "Philip Gray has created something truly special. The Mass explores identity and community in ways that feel fresh and profoundly relevant.",
  author: "Elena Smith",
  role: "Cultural Blogger",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1503120/img/image_q433sppw.jpg"
}];
export default function AudienceTestimonials() {
  const [isPaused, setIsPaused] = useState(false);
  const [api, setApi] = useState<any>(null);
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (!api) return;
    const startAutoplay = () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        if (!isPaused) {
          api.scrollNext();
        }
      }, 4000);
    };
    startAutoplay();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [api, isPaused]);
  return <section id="audience-testimonials" ref={ref} className="bg-background py-16 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-4xl">
        <div className={`mb-12 text-center transition-all duration-[350ms] ease-[var(--ease-smooth)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-3">
            Audience Testimonials
          </h2>
          <p className="text-muted-foreground text-lg">
            Hear what our audiences have to say about The Mass
          </p>
        </div>

        <div className={`transition-all duration-[350ms] ease-[var(--ease-smooth)] delay-100 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)}>
          <Carousel setApi={setApi} opts={{
          align: "start",
          loop: true,
          axis: "y"
        }} orientation="vertical" className="w-full">
            <CarouselContent className="h-[420px] sm:h-[380px]">
              {testimonials.map((testimonial, i) => <CarouselItem key={testimonial.id} data-index={i} className="basis-full">
                  <Card className="border border-border shadow-soft hover:shadow-medium transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)]">
                    <CardContent className="flex flex-col items-center text-center p-8 space-y-6">
                      <Quote className="text-primary w-10 h-10" />
                      
                      <blockquote className="text-foreground text-lg sm:text-xl leading-relaxed font-sans">
                        "{testimonial.quote}"
                      </blockquote>

                      <div className="flex flex-col items-center space-y-3 pt-4">
                        <SafeHydrate><Avatar className="w-16 h-16 border-2 border-primary">
                          <AvatarImage src={testimonial.avatar} alt={testimonial.author} />
                          <AvatarFallback className="bg-primary/10 text-primary font-semibold text-lg">
                            {testimonial.author.split(' ').map(n => n[0]).join('')}
                          </AvatarFallback>
                        </Avatar></SafeHydrate>
                        
                        <div>
                          <p className="font-display font-semibold text-foreground text-lg">
                            {testimonial.author}
                          </p>
                          <p className="text-muted-foreground text-sm">
                            {testimonial.role}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </CarouselItem>)}
            </CarouselContent>
          </Carousel>

          <div className="flex justify-center gap-2 mt-6">
            {testimonials.map((_, i) => <Button key={i} onClick={() => api?.scrollTo(i)} variant="ghost" size="icon" className="w-2 h-2 rounded-full bg-muted hover:bg-primary transition-colors duration-[var(--duration-fast)] p-0 min-w-0" aria-label={`Go to testimonial ${i + 1}`} />)}
          </div>
        </div>
      </div>
    </section>;
}